import type { ReactNode } from 'react'
import type { PromoCodeDetail, PromoRedemption } from '@manaro/contracts'
import { ApiError } from '@/shared/api/client.ts'
import { formatDate } from '@/shared/lib/format.ts'
import { Badge } from '@/shared/ui/badge.tsx'
import { Button } from '@/shared/ui/button.tsx'
import { Card, CardHeader } from '@/shared/ui/card.tsx'
import { CardFact, CardFacts, DesktopTable, MobileCard, MobileCardList } from '@/shared/ui/responsive-table.tsx'
import { Alert, EmptyState } from '@/shared/ui/states.tsx'
import { expiryText, promoTerms, redemptionResult, sourceLabel, STATUS_LABELS, STATUS_TONES, usageText } from '../labels.ts'
import { useUpdatePromoCode } from '../use-promo-codes.ts'
import { CopyCodeButton } from './copy-code-button.tsx'

interface PromoCodeViewProps {
  promo: PromoCodeDetail
  canManage: boolean
  onEditLimits: () => void
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dt className="text-[12.5px] text-ink-muted">{label}</dt>
      <dd className="min-w-0 text-[14.5px] font-medium [overflow-wrap:anywhere]">{children}</dd>
    </div>
  )
}

const salonName = (redemption: PromoRedemption): string => redemption.salon?.title ?? 'Салон ещё не создан'
const when = (redemption: PromoRedemption): string => {
  const at = redemption.appliedAt ?? redemption.createdAt
  return at ? formatDate(at) : '—'
}

function Redemptions({ promo }: { promo: PromoCodeDetail }) {
  return (
    <>
      <MobileCardList label="Кто ввёл код">
        {promo.redemptions.map((redemption) => (
          <MobileCard key={redemption.id}>
            <div className="text-[15px] font-semibold [overflow-wrap:anywhere]">{salonName(redemption)}</div>
            <CardFacts>
              <CardFact label="Где ввели">{sourceLabel(redemption.source)}</CardFact>
              <CardFact label="Когда">{when(redemption)}</CardFact>
              <CardFact label="Результат" wide>
                {redemptionResult(redemption, promo)}
              </CardFact>
            </CardFacts>
          </MobileCard>
        ))}
      </MobileCardList>

      <DesktopTable>
        <table className="w-full min-w-[640px] text-left text-[13.5px]">
          <thead className="border-b border-line-soft text-[12px] text-ink-muted">
            <tr>
              {['Салон', 'Где ввели', 'Когда', 'Результат'].map((title) => (
                <th key={title} scope="col" className="px-3.5 py-3 font-medium whitespace-nowrap first:pl-5">
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft">
            {promo.redemptions.map((redemption) => (
              <tr key={redemption.id}>
                <td className="py-3 pr-3.5 pl-5 font-medium">
                  {salonName(redemption)}
                  {redemption.salon && <span className="ml-1.5 text-[12px] font-normal text-ink-faint">#{redemption.salon.id}</span>}
                </td>
                <td className="px-3.5 py-3">{sourceLabel(redemption.source)}</td>
                <td className="px-3.5 py-3 whitespace-nowrap">{when(redemption)}</td>
                <td className="px-3.5 py-3">{redemptionResult(redemption, promo)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DesktopTable>
    </>
  )
}

export function PromoCodeView({ promo, canManage, onEditLimits }: PromoCodeViewProps) {
  const toggle = useUpdatePromoCode()

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="font-mono text-[25px] leading-tight font-semibold tracking-[0.02em] [overflow-wrap:anywhere] md:text-[30px]">
            {promo.code}
          </h1>
          <Badge tone={STATUS_TONES[promo.status]}>{STATUS_LABELS[promo.status]}</Badge>
        </div>
        <p className="text-[16px] font-medium">{promoTerms(promo)}</p>
        <div className="flex flex-wrap gap-2 max-sm:[&>*]:flex-1">
          <CopyCodeButton code={promo.code} />
          {canManage && (
            <>
              <Button variant="secondary" size="sm" onClick={onEditLimits} className="max-md:min-h-11">
                Лимит и срок
              </Button>
              <Button
                variant="secondary"
                size="sm"
                loading={toggle.isPending}
                onClick={() => toggle.mutate({ id: promo.id, request: { isActive: !promo.isActive } })}
                className="max-md:min-h-11"
              >
                {promo.isActive ? 'Выключить' : 'Включить'}
              </Button>
            </>
          )}
        </div>
        {toggle.error && (
          <Alert>{toggle.error instanceof ApiError ? toggle.error.message : 'Не получилось — попробуйте ещё раз.'}</Alert>
        )}
        {!promo.isActive && (
          <p className="text-[13px] text-ink-muted">
            Выключенный код салоны не введут. Кто уже ввёл — сохраняет скидку или подаренный тариф.
          </p>
        )}
      </header>

      <Card className="px-4 py-4 md:px-5">
        <dl className="grid grid-cols-2 gap-x-5 gap-y-4 md:grid-cols-4">
          <Fact label="Ввели">
            <span className="tabular">{usageText(promo)}</span>
            {promo.maxRedemptions === null && <span className="font-normal text-ink-muted"> · без лимита</span>}
          </Fact>
          <Fact label="Действует">{expiryText(promo)}</Fact>
          <Fact label="Создал">{promo.createdBy}</Fact>
          <Fact label="Создан">{promo.createdAt ? formatDate(promo.createdAt) : '—'}</Fact>
          {promo.note && (
            <div className="col-span-2 md:col-span-4">
              <Fact label="Заметка">{promo.note}</Fact>
            </div>
          )}
        </dl>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader title="Кто ввёл код" className="mb-0 px-4 pt-4.5 pb-3.5 md:px-5" />
        {promo.redemptions.length === 0 ? (
          <EmptyState title="Пока никто" description="Как только салон введёт код в CRM, он появится здесь." />
        ) : (
          <Redemptions promo={promo} />
        )}
      </Card>
    </div>
  )
}
